// @flow
import './index.less';

import {CardElement, useElements, useStripe} from '@stripe/react-stripe-js';

import {Alert, Button} from 'antd';
import classNames from 'classnames';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import React, {useCallback, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import useAnalytics, {event} from '_hooks/useAnalytics';
import {createSetupIntent, detachPaymentMethod} from '_utils/stripe';

type Props = {
  disabled?: boolean,
  hidden?: boolean,
  showHeader?: boolean,
  onSuccess?: (paymentMethod: Object) => void,
  onCancel?: () => void,
};

const PaymentMethodAddForm = ({
  disabled = false,
  hidden = false,
  showHeader = false,
  onSuccess = () => {},
  onCancel = () => {},
}: Props): React$Node => {
  const {t} = useTranslation();
  const {track} = useAnalytics();

  const [isValid, setIsValid] = useState(false);
  const [paymentMethodErr, setPaymentMethodErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const handleCardChange = useCallback(({complete, error}) => {
    setIsValid(complete && !error);
  }, []);

  const handleSubmit = useCallback(
    e =>
      (async () => {
        e.preventDefault();

        if (!stripe || !elements) {
          // Stripe.js has not loaded yet. Make sure to disable
          // form submission until Stripe.js has loaded.
          return;
        }

        setPaymentMethodErr(null);
        setLoading(true);

        try {
          const cardElement = elements.getElement(CardElement);
          const {error, paymentMethod} = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });

          if (error) {
            throw new Error(error.msg);
          }

          const setupIntent = await createSetupIntent(uuidv4(), paymentMethod.id);
          if (setupIntent.status === 'succeeded') {
            track({
              name: event.af.addPaymentInfo,
              data: {
                af_success: true,
              },
            });
            if (onSuccess) onSuccess(paymentMethod);
            return;
          }

          const scaResult = await stripe.confirmCardSetup(setupIntent.clientSecret, {
            payment_method: paymentMethod.id,
          });

          if (scaResult.error) {
            await detachPaymentMethod(uuidv4(), paymentMethod.id);
            throw new Error(scaResult.error.message);
          }

          if (onSuccess) onSuccess(paymentMethod);
        } catch (err) {
          setPaymentMethodErr(err);
        } finally {
          setLoading(false);
        }
      })(),
    [elements, stripe, onSuccess, track],
  );

  return (
    <form
      className={classNames('payment-method-add-form', {
        'payment-method-add-form-hidden': hidden,
      })}
      onSubmit={handleSubmit}>
      {showHeader && (
        <div className="header">
          <div className="title">{t('Common_AddNewCard')}</div>
          <div className="cancel-button" onClick={onCancel}>
            {t('Common_Cancel')}
          </div>
        </div>
      )}
      <div
        style={{
          padding: '0.8rem',
          backgroundColor: '#160F10',
          marginBottom: '1rem',
        }}>
        <CardElement
          options={{
            disabled,
            hidePostalCode: true,
            iconStyle: 'solid',
            style: {
              base: {
                iconColor: '#fff',
                fontSize: '15px',
                fontSmoothing: 'antialiased',
                color: '#fff',
              },
              invalid: {
                iconColor: '#eb5757',
                color: '#eb5757',
              },
            },
          }}
          onChange={handleCardChange}
        />
      </div>
      <Button
        className="add-btn"
        type="primary"
        htmlType="submit"
        disabled={disabled || !isValid}
        block
        loading={loading}
      >
        {t('Common_VerifyCard')}
      </Button>

      {paymentMethodErr && (
        <Alert
          message={paymentMethodErr?.message}
          type="error"
          style={{textAlign: 'left', marginTop: '1rem', marginBottom: '1rem', color: '#a22020'}}
        />
      )}
    </form>
  );
};

export default PaymentMethodAddForm;
